import { FURNITURE_INFO, type FurnitureKind, type OfficeFurniture, type OfficeLayout, type OfficeRoom, type OfficeTemplate } from './types';

export const OFFICE_TEMPLATE_INFO: Record<OfficeTemplate, { label: string; description: string }> = {
  office: { label: 'Office', description: 'An open commons with desks, two offices, a boardroom, a huddle room and a kitchen.' },
  lounge: { label: 'Lounge', description: 'Sofas, round tables and a quiet corner for coffee and casual talk.' },
  event: { label: 'Event', description: 'A stage with rows of seats, a backstage office and a foyer.' },
};

export interface OfficeIdGen {
  room(): string;
  item(): string;
}
type IdGen = OfficeIdGen;

/** Builds a starting layout for a template. `ids` supplies stable ids for rooms and items. */
export function buildTemplate(template: OfficeTemplate, ids: IdGen): OfficeLayout {
  switch (template) {
    case 'lounge':
      return lounge(ids);
    case 'event':
      return event(ids);
    default:
      return office(ids);
  }
}

function item(ids: IdGen, kind: FurnitureKind, x: number, y: number, extra: Partial<OfficeFurniture> = {}): OfficeFurniture {
  const info = FURNITURE_INFO[kind];
  return { id: ids.item(), kind, x, y, w: info.w, h: info.h, rotation: 0, ...extra };
}

function room(ids: IdGen, name: string, kind: OfficeRoom['kind'], x: number, y: number, w: number, h: number, extra: Partial<OfficeRoom> = {}): OfficeRoom {
  return { id: ids.room(), name, kind, x, y, w, h, ...extra };
}

/**
 * A desk with a chair in front of it. `facing` is where the chair sits. A chair at rotation 0 has its
 * back at the top and faces down, so a chair below its desk is turned round to face it.
 */
function workstation(ids: IdGen, x: number, y: number, facing: 'down' | 'up' = 'down'): OfficeFurniture[] {
  const desk = item(ids, 'desk', x, y);
  const chair = facing === 'down' ? item(ids, 'chair', x + 40, y + 56, { rotation: 180 }) : item(ids, 'chair', x + 40, y - 36);
  return [desk, chair];
}

function office(ids: IdGen): OfficeLayout {
  const items: OfficeFurniture[] = [];
  // Two banks of facing desks, with room between their outward-facing chairs.
  for (const y of [180, 460]) {
    for (const x of [240, 520, 800]) {
      items.push(...workstation(ids, x, y, 'up'));
      items.push(...workstation(ids, x, y + 120, 'down'));
    }
  }
  items.push(item(ids, 'rug', 400, 700, { w: 520, h: 200 }));
  items.push(item(ids, 'sofa', 440, 720));
  items.push(item(ids, 'sofa', 440, 820, { rotation: 180 }));
  items.push(item(ids, 'table-round', 700, 760, { w: 100, h: 100 }));
  items.push(item(ids, 'plant', 120, 120));
  items.push(item(ids, 'plant', 1240, 120));
  items.push(item(ids, 'plant', 120, 832));
  items.push(item(ids, 'whiteboard', 560, 92));

  // Offices on the top right, doors facing the commons.
  items.push(item(ids, 'desk', 1600, 200, { rotation: 90 }));
  items.push(item(ids, 'chair', 1680, 240, { rotation: 90 }));
  items.push(item(ids, 'bookshelf', 1500, 100));
  items.push(item(ids, 'plant', 1800, 340));
  items.push(item(ids, 'desk', 2020, 200, { rotation: 90 }));
  items.push(item(ids, 'chair', 2100, 240, { rotation: 90 }));
  items.push(item(ids, 'bookshelf', 1920, 100));
  items.push(item(ids, 'plant', 2220, 340));

  // Boardroom: a long table with chairs around it and a screen on the wall.
  items.push(item(ids, 'table-long', 1760, 740, { w: 320, h: 120 }));
  for (const x of [1790, 1880, 1970]) {
    items.push(item(ids, 'chair', x, 690));
    items.push(item(ids, 'chair', x, 870, { rotation: 180 }));
  }
  items.push(item(ids, 'chair', 1710, 780, { rotation: 270 }));
  items.push(item(ids, 'chair', 2090, 780, { rotation: 90 }));
  items.push(item(ids, 'screen', 1860, 512));
  items.push(item(ids, 'whiteboard', 2160, 540, { rotation: 90 }));

  // Huddle room: a round table with four chairs.
  items.push(item(ids, 'table-round', 300, 1060));
  items.push(item(ids, 'chair', 340, 1010));
  items.push(item(ids, 'chair', 340, 1190, { rotation: 180 }));
  items.push(item(ids, 'chair', 250, 1100, { rotation: 270 }));
  items.push(item(ids, 'chair', 430, 1100, { rotation: 90 }));
  items.push(item(ids, 'whiteboard', 320, 940));

  // Kitchen: a counter, coffee and a table.
  items.push(item(ids, 'counter', 760, 960));
  items.push(item(ids, 'coffee', 1010, 970));
  items.push(item(ids, 'table-round', 900, 1120, { w: 100, h: 100 }));
  items.push(item(ids, 'chair', 930, 1070));
  items.push(item(ids, 'chair', 930, 1230, { rotation: 180 }));
  items.push(item(ids, 'plant', 1200, 1240));

  return {
    version: 1,
    width: 2400,
    height: 1400,
    floor: { color: '#e5d8c6', pattern: 'wood' },
    palette: 'oak',
    spawn: { x: 660, y: 688 },
    rooms: [
      room(ids, 'Commons', 'open', 80, 80, 1240, 820, { color: '#f7f6f2' }),
      room(ids, 'Office 1', 'office', 1440, 80, 400, 340, { door: { side: 'bottom', offset: 200 } }),
      room(ids, 'Office 2', 'office', 1860, 80, 400, 340, { door: { side: 'bottom', offset: 200 } }),
      room(ids, 'Boardroom', 'meeting', 1440, 500, 820, 420, { door: { side: 'left', offset: 210 }, capacity: 12 }),
      room(ids, 'Huddle', 'meeting', 80, 920, 520, 400, { door: { side: 'right', offset: 200 }, capacity: 4 }),
      room(ids, 'Kitchen', 'lounge', 680, 940, 620, 380, { color: '#eef4ee' }),
    ],
    items,
    walls: [],
  };
}

function lounge(ids: IdGen): OfficeLayout {
  const items: OfficeFurniture[] = [];
  items.push(item(ids, 'rug', 300, 300, { w: 520, h: 300 }));
  items.push(item(ids, 'sofa', 340, 320));
  items.push(item(ids, 'sofa', 340, 520, { rotation: 180 }));
  items.push(item(ids, 'armchair', 560, 330));
  items.push(item(ids, 'armchair', 560, 490));
  items.push(item(ids, 'table-round', 700, 400, { w: 90, h: 90 }));
  items.push(item(ids, 'rug', 1000, 300, { w: 520, h: 300 }));
  items.push(item(ids, 'table-round', 1140, 380));
  items.push(item(ids, 'chair', 1180, 330));
  items.push(item(ids, 'chair', 1180, 510, { rotation: 180 }));
  items.push(item(ids, 'chair', 1090, 420, { rotation: 270 }));
  items.push(item(ids, 'chair', 1270, 420, { rotation: 90 }));
  items.push(item(ids, 'counter', 300, 820));
  items.push(item(ids, 'coffee', 560, 830));
  items.push(item(ids, 'plant', 120, 120));
  items.push(item(ids, 'plant', 1640, 120));
  items.push(item(ids, 'plant', 120, 1040));
  items.push(item(ids, 'plant', 1640, 1040));
  items.push(item(ids, 'bookshelf', 1400, 820));
  items.push(item(ids, 'lamp', 900, 840));
  items.push(item(ids, 'armchair', 1100, 880));
  items.push(item(ids, 'armchair', 1200, 880));
  return {
    version: 1,
    width: 1800,
    height: 1200,
    floor: { color: '#e0cdb5', pattern: 'wood' },
    palette: 'walnut',
    spawn: { x: 900, y: 700 },
    rooms: [
      room(ids, 'Living room', 'lounge', 80, 80, 1640, 640, { color: '#f9f5ee' }),
      room(ids, 'Coffee bar', 'lounge', 80, 760, 760, 360, { color: '#f3efe8' }),
      room(ids, 'Reading nook', 'quiet', 1000, 760, 720, 360, { door: { side: 'left', offset: 180 } }),
    ],
    items,
    walls: [],
  };
}

function event(ids: IdGen): OfficeLayout {
  const items: OfficeFurniture[] = [];
  items.push(item(ids, 'screen', 1140, 112, { w: 200 }));
  items.push(item(ids, 'table-long', 1040, 180, { w: 320, h: 80 }));
  items.push(item(ids, 'chair', 1120, 140));
  items.push(item(ids, 'chair', 1220, 140));
  for (const row of [0, 1, 2, 3, 4]) {
    for (const col of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]) {
      items.push(item(ids, 'chair', 700 + col * 110, 460 + row * 90, { rotation: 180 }));
    }
  }
  items.push(item(ids, 'plant', 560, 120));
  items.push(item(ids, 'plant', 1800, 120));
  items.push(item(ids, 'desk', 220, 220, { rotation: 90 }));
  items.push(item(ids, 'chair', 300, 260, { rotation: 90 }));
  items.push(item(ids, 'bookshelf', 120, 100));
  items.push(item(ids, 'counter', 200, 1100));
  items.push(item(ids, 'coffee', 460, 1110));
  items.push(item(ids, 'table-round', 1800, 1120, { w: 100, h: 100 }));
  items.push(item(ids, 'table-round', 2060, 1120, { w: 100, h: 100 }));
  return {
    version: 1,
    width: 2400,
    height: 1400,
    floor: { color: '#e6ddce', pattern: 'tiles' },
    palette: 'coastal',
    spawn: { x: 1200, y: 1180 },
    rooms: [
      room(ids, 'Stage', 'stage', 560, 80, 1300, 300, { color: '#e9e4f8' }),
      room(ids, 'Seating', 'open', 560, 420, 1300, 520, { color: '#f3f3f8' }),
      room(ids, 'Backstage', 'office', 80, 80, 400, 400, { door: { side: 'right', offset: 200 } }),
      room(ids, 'Foyer', 'lounge', 80, 980, 2240, 340, { color: '#f6f3ee' }),
    ],
    items,
    walls: [],
  };
}
